import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { positionAPI, departmentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function slugify(text) {
  return (text || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[đĐ]/g, m => m === 'đ' ? 'd' : 'D')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const LEVELS = [
  { value: 'junior',    label: 'Nhân viên',             color: '#64748b', bg: '#f1f5f9' },
  { value: 'mid',       label: 'Chuyên viên',           color: '#0891b2', bg: '#e0f2fe' },
  { value: 'senior',    label: 'Chuyên viên cao cấp',   color: '#7c3aed', bg: '#ede9fe' },
  { value: 'lead',      label: 'Trưởng nhóm',           color: '#0f766e', bg: '#ccfbf1' },
  { value: 'manager',   label: 'Quản lý',               color: '#b45309', bg: '#fef9c3' },
  { value: 'director',  label: 'Giám đốc',              color: '#1e3a8a', bg: '#dbeafe' },
  { value: 'c_level',   label: 'Ban lãnh đạo',          color: '#9f1239', bg: '#ffe4e6' },
];

const levelMap = Object.fromEntries(LEVELS.map(l => [l.value, l]));
const fmt = n => n ? new Intl.NumberFormat('vi-VN').format(Number(n)) : '0';

const EMPTY_FORM = { title: '', level: 'mid', department_id: '', salary_min: '', salary_max: '' };

export default function PositionsPage() {
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  const canEdit = ['admin', 'hr_manager'].includes(user?.role);
  const canDelete = user?.role === 'admin';

  const load = () => {
    setLoading(true);
    Promise.all([positionAPI.list(), departmentAPI.list()])
      .then(([posRes, deptRes]) => {
        setPositions(posRes.data.data);
        setDepartments(deptRes.data.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (pos) => {
    setEditTarget(pos);
    setForm({
      title: pos.title,
      level: pos.level || 'mid',
      department_id: pos.department_id || '',
      salary_min: pos.salary_min || '',
      salary_max: pos.salary_max || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setFormError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (form.salary_min && form.salary_max && Number(form.salary_min) > Number(form.salary_max)) {
      setFormError('Lương tối thiểu không được lớn hơn lương tối đa');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        level: form.level,
        department_id: form.department_id || null,
        salary_min: form.salary_min || 0,
        salary_max: form.salary_max || 0,
      };
      if (editTarget) {
        await positionAPI.update(editTarget.id, payload);
      } else {
        await positionAPI.create(payload);
      }
      closeForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Lỗi lưu chức danh');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (pos) => {
    setDeleteError('');
    if (!window.confirm(`Xóa chức danh "${pos.title}"?`)) return;
    try {
      await positionAPI.delete(pos.id);
      load();
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Lỗi xóa chức danh');
    }
  };

  const filtered = positions.filter(p =>
    (!filterLevel || p.level === filterLevel) &&
    (!filterDept || String(p.department_id) === filterDept)
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>💼 Chức danh</h1>
          <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
            Quản lý vị trí và chức danh trong tổ chức
          </p>
        </div>
        {canEdit && (
          <button onClick={openCreate} style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg,#1e3a8a,#0891b2)',
            color: '#fff', border: 'none', borderRadius: 8,
            cursor: 'pointer', fontWeight: 600, fontSize: 14,
          }}>
            + Thêm chức danh
          </button>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Tổng chức danh', value: positions.length, color: '#1e3a8a' },
          { label: 'Đang có người', value: positions.filter(p => p.employee_count > 0).length, color: '#16a34a' },
          { label: 'Chưa có người', value: positions.filter(p => !p.employee_count || p.employee_count == 0).length, color: '#f59e0b' },
          { label: 'Tổng nhân viên', value: positions.reduce((s, p) => s + (parseInt(p.employee_count) || 0), 0), color: '#0891b2' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            flex: 1, minWidth: 160, background: '#fff', borderRadius: 10,
            padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}`,
          }}>
            <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Level badges overview */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '14px 20px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginRight: 4 }}>Theo cấp độ:</span>
        {LEVELS.map(lv => {
          const cnt = positions.filter(p => p.level === lv.value).length;
          if (cnt === 0) return null;
          return (
            <span key={lv.value} style={{
              background: lv.bg, color: lv.color, padding: '4px 12px',
              borderRadius: 12, fontSize: 12, fontWeight: 600,
            }}>
              {lv.label}: {cnt}
            </span>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)}
          style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}>
          <option value="">Tất cả cấp độ</option>
          {LEVELS.map(lv => <option key={lv.value} value={lv.value}>{lv.label}</option>)}
        </select>
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)}
          style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}>
          <option value="">Tất cả phòng ban</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <span style={{ padding: '8px 14px', background: '#f1f5f9', borderRadius: 8, fontSize: 13, color: '#64748b' }}>
          {filtered.length} chức danh
        </span>
      </div>

      {/* Delete error */}
      {deleteError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
          padding: '10px 16px', color: '#dc2626', fontSize: 13, marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>⚠️ {deleteError}</span>
          <button onClick={() => setDeleteError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16 }}>×</button>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              {['Chức danh', 'Cấp độ', 'Phòng ban', 'Khoảng lương', 'Nhân viên', canEdit ? 'Hành động' : ''].filter(Boolean).map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>⏳ Đang tải...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Chưa có chức danh nào</td></tr>
            ) : filtered.map(pos => {
              const lv = levelMap[pos.level] || levelMap['mid'];
              return (
                <tr key={pos.id}
                  style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                  onClick={() => navigate(`/positions/${pos.id}/${slugify(pos.title)}`)}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                        background: `${lv.bg}`, border: `1px solid ${lv.color}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16,
                      }}>
                        💼
                      </div>
                      <div style={{ fontWeight: 600, color: '#1d4ed8' }}>{pos.title}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>Nhấn để xem chi tiết →</div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      background: lv.bg, color: lv.color,
                      padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    }}>
                      {lv.label}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>
                    {pos.department_name
                      ? <span style={{ background: '#f8fafc', padding: '3px 10px', borderRadius: 6, fontSize: 12 }}>{pos.department_name}</span>
                      : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {pos.salary_min > 0 || pos.salary_max > 0 ? (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                          {fmt(pos.salary_min)} — {fmt(pos.salary_max)} ₫
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>tháng</div>
                      </div>
                    ) : (
                      <span style={{ color: '#cbd5e1', fontSize: 12 }}>Chưa thiết lập</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: pos.employee_count > 0 ? '#dcfce7' : '#f1f5f9',
                      color: pos.employee_count > 0 ? '#16a34a' : '#94a3b8',
                      padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    }}>
                      👤 {pos.employee_count || 0} NV
                    </span>
                  </td>
                  {canEdit && (
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={e => { e.stopPropagation(); openEdit(pos); }} style={{
                          padding: '5px 12px', background: '#eff6ff', color: '#1d4ed8',
                          border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                        }}>
                          ✏️ Sửa
                        </button>
                        {canDelete && (
                          <button onClick={e => { e.stopPropagation(); handleDelete(pos); }} style={{
                            padding: '5px 12px', background: '#fef2f2', color: '#dc2626',
                            border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                          }}>
                            🗑️ Xóa
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal Create / Edit */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 24px', fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
              {editTarget ? '✏️ Sửa chức danh' : '💼 Thêm chức danh mới'}
            </h3>

            {formError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
                ⚠️ {formError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Tên chức danh <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input type="text" required value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="VD: Senior Software Engineer, HR Specialist..."
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>

              {/* Level + Department */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                    Cấp độ <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select value={form.level} onChange={e => setForm({ ...form, level: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
                    {LEVELS.map(lv => <option key={lv.value} value={lv.value}>{lv.label}</option>)}
                  </select>
                  {form.level && (
                    <div style={{ marginTop: 6 }}>
                      <span style={{ background: levelMap[form.level]?.bg, color: levelMap[form.level]?.color, padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                        {levelMap[form.level]?.label}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                    Phòng ban
                  </label>
                  <select value={form.department_id} onChange={e => setForm({ ...form, department_id: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
                    <option value="">— Không gắn phòng ban —</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Salary range */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Khoảng lương (₫/tháng)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <input type="number" min="0" value={form.salary_min}
                      onChange={e => setForm({ ...form, salary_min: e.target.value })}
                      placeholder="Tối thiểu"
                      style={{ width: '100%', padding: '10px 12px', border: `1px solid ${formError.includes('tối thiểu') ? '#fca5a5' : '#d1d5db'}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                      {form.salary_min ? `${fmt(form.salary_min)} ₫` : 'Chưa nhập'}
                    </div>
                  </div>
                  <div>
                    <input type="number" min="0" value={form.salary_max}
                      onChange={e => setForm({ ...form, salary_max: e.target.value })}
                      placeholder="Tối đa"
                      style={{ width: '100%', padding: '10px 12px', border: `1px solid ${formError.includes('tối thiểu') ? '#fca5a5' : '#d1d5db'}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                      {form.salary_max ? `${fmt(form.salary_max)} ₫` : 'Chưa nhập'}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeForm}
                  style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff', fontSize: 14 }}>
                  Hủy
                </button>
                <button type="submit" disabled={submitting} style={{
                  padding: '10px 24px',
                  background: submitting ? '#93c5fd' : 'linear-gradient(135deg,#1e3a8a,#0891b2)',
                  color: '#fff', border: 'none', borderRadius: 8,
                  cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14,
                }}>
                  {submitting ? '⏳ Đang lưu...' : (editTarget ? 'Cập nhật' : 'Tạo chức danh')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
